import { ButtonBase, styled, Typography } from "@mui/material"
import { Conditional, Stack } from "src/shared/components"
import Link from "src/shared/components/Link"
import SidebarSubMenu from "./sidebar-subMenu"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { useLocation, useNavigate } from "react-router-dom"
import ROUTE_PATHS from "src/routes/route-path"

import { ISidebarItem, ISidebarItemCommon } from "."
import { useGlobalConfig } from "src/shared/context/globalConfigContext"
import { useState } from "react"
import SidebarSubmenuPopover from "./sidebar-submenu-popover"

export type IhandleOpenSubMenuPopover = {
  el: HTMLElement | null
  sidebarItem: ISidebarItem | null
}
const SidebarMenu = ({ sidebarItems }: { sidebarItems: ISidebarItem[] }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isSidebarOpen, toggleSubMenu, subMenu } = useGlobalConfig()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [openSubMenuItem, setOpenSubMenuItem] = useState<ISidebarItem | null>()
  const isActive = (sidebarItem: ISidebarItem) => {
    if (!sidebarItem.href) return !!sidebarItem?.children?.some((e) => location.pathname.includes(e.href as string))
    const itemsSplit = sidebarItem.href?.split("/").filter(Boolean)
    if (itemsSplit?.length === 1) return location.pathname === ROUTE_PATHS.DASHBOARD
    else {
      return location.pathname.includes(sidebarItem.href)
    }
  }
  const handleSubMenuToggle = (title: string) => {
    toggleSubMenu(subMenu === title ? null : title)
  }

  const handleOpenSubMenuPopover = ({ el, sidebarItem }: IhandleOpenSubMenuPopover) => {
    if (isSidebarOpen) return
    setOpenSubMenuItem(sidebarItem)
    setAnchorEl(el)
  }
  const handleCloseSubMenoPopover = () => {
    setAnchorEl(null)
  }
  return (
    <Stack
      direction={"column"}
      spacing={1}
      width={"100%"}
      alignItems={"center"}
      p={1.25}
      flex={1}
      className="custom-scroll hovered-scrollbar"
      sx={{
        overflowX: "hidden",
        overflowY: "auto"
      }}
    >
      {sidebarItems.map((item, index) => (
        <SideBarMenuItem
          key={`${item?.title}-${index}`}
          isSidebarOpen={isSidebarOpen}
          isSubmenuOpen={item.title === subMenu}
          onMouseEnter={(e) => handleOpenSubMenuPopover({ el: e.currentTarget, sidebarItem: item })}
          onMouseLeave={handleCloseSubMenoPopover}
        >
          <Link to={item.href as string}>
            <SideBarMenuItemButton
              onClick={() => {
                if (item?.href) navigate(item.href)
                handleSubMenuToggle(isSidebarOpen ? item.title : "")
              }}
              isSidebarOpen={isSidebarOpen}
              disableRipple
              isActive={isActive(item)}
            >
              {item.icon}
              <SidebarMenuItemTitle variant="body2" isSidebarOpen={isSidebarOpen}>
                {item.title}
              </SidebarMenuItemTitle>

              {/* chevron icon for sub menu */}
              <Conditional condition={!!(item.children?.length && !!isSidebarOpen)}>
                <StyledSidebarChevronIcon isSubmenuOpen={item.title === subMenu} />
              </Conditional>
            </SideBarMenuItemButton>
          </Link>

          {/* render sub menu if children exist */}
          <Conditional condition={!!item.children?.length}>
            <SidebarSubMenu isOpenSubMenu={item.title === subMenu} subMenu={item.children as ISidebarItemCommon[]} />
          </Conditional>
        </SideBarMenuItem>
      ))}
      <SidebarSubmenuPopover
        sidebarItem={openSubMenuItem as ISidebarItem}
        anchorEl={anchorEl}
        onClose={handleCloseSubMenoPopover}
        handleOpenSubMenuPopover={handleOpenSubMenuPopover}
      />
    </Stack>
  )
}

const SideBarMenuItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isSidebarOpen" && prop !== "isSubmenuOpen"
})<{ isSubmenuOpen: boolean; isSidebarOpen: boolean }>(({ theme, isSidebarOpen, isSubmenuOpen }) => ({
  background: isSubmenuOpen ? (theme.palette.sidebar?.submenuBackground ?? "") : "initial",
  width: isSidebarOpen ? "100%" : "46px",
  borderRadius: "8px"
}))

const SidebarMenuItemTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSidebarOpen"
})<{ isSidebarOpen: boolean }>(({ isSidebarOpen }) => ({
  fontWeight: 400,
  marginRight: isSidebarOpen ? "auto" : 0,
  display: isSidebarOpen ? "inline-block" : "none"
}))

export const SideBarMenuItemButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "isSidebarOpen" && prop !== "isActive"
})<{ isSidebarOpen: boolean; isActive: boolean }>(({ theme, isSidebarOpen, isActive }) => ({
  width: "100%",
  height: "46px",
  padding: "0px 6px",
  justifyContent: isSidebarOpen ? "left" : "center",
  borderRadius: "8px",
  gap: "6px",
  zIndex: 5,
  color: theme.palette.sidebar?.contrastText,
  background: isActive ? theme.palette.sidebar?.active : "transparent",
  "&:hover": {
    background: theme.palette.sidebar?.hover
  },
  transition: theme.transitions.create(["width"], {
    duration: theme.transitions.duration.standard
  })
}))

const StyledSidebarChevronIcon = styled(ChevronRightIcon, {
  shouldForwardProp: (prop) => prop !== "isSubmenuOpen"
})<{ isSubmenuOpen: boolean }>(({ theme, isSubmenuOpen }) => ({
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.standard
  }),
  transform: isSubmenuOpen ? "rotate(90deg)" : "rotate(0deg)"
}))

export default SidebarMenu
